import React, { useMemo, useCallback } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import type {
  BaseAtomProps,
  ComponentSize,
  ComponentColor,
} from "@/components/types";

export interface QuantityControlProps extends BaseAtomProps {
  /**
   * Current quantity value
   */
  value: number;

  /**
   * Callback when quantity changes
   */
  onChange: (newValue: number) => void;

  /**
   * Minimum quantity allowed (default: 1)
   */
  min?: number;

  /**
   * Maximum quantity allowed (optional)
   */
  max?: number;

  /**
   * Component size
   */
  size?: ComponentSize;

  /**
   * Button color scheme
   */
  color?: ComponentColor;

  /**
   * Whether the component is disabled
   */
  disabled?: boolean;

  /**
   * Custom styling for the container
   */
  containerSx?: React.ComponentProps<typeof Box>["sx"];

  /**
   * Custom styling for the quantity display
   */
  quantitySx?: React.ComponentProps<typeof Typography>["sx"];

  /**
   * Custom styling for the buttons
   */
  buttonSx?: React.ComponentProps<typeof IconButton>["sx"];
}

const QuantityControlComponent: React.FC<QuantityControlProps> = ({
  value,
  onChange,
  min = 1,
  max,
  size = "medium",
  color = "primary",
  disabled = false,
  containerSx,
  quantitySx,
  buttonSx,
  className,
  ...props
}) => {
  // Size configurations
  const sizeConfig = useMemo(
    () => ({
      small: {
        height: 40,
        buttonSize: 30,
        fontSize: 18,
        gap: 0.5,
      },
      medium: {
        height: 52,
        buttonSize: 44,
        fontSize: 20,
        gap: { xs: 0.5, x2l: 1 },
      },
      large: {
        height: 60,
        buttonSize: 42,
        fontSize: 22,
        gap: 2,
      },
    }),
    [size]
  );

  const config = sizeConfig[size];

  // Determine if buttons should be disabled
  const isDecrementDisabled = disabled || value <= min;
  const isIncrementDisabled = disabled || (max !== undefined && value >= max);

  // Handle button clicks
  const handleDecrement = useCallback(() => {
    if (!isDecrementDisabled) {
      const newValue = Math.max(min, value - 1);
      onChange(newValue);
    }
  }, [isDecrementDisabled, min, value, onChange]);

  const handleIncrement = useCallback(() => {
    if (!isIncrementDisabled) {
      const newValue = max !== undefined ? Math.min(max, value + 1) : value + 1;
      onChange(newValue);
    }
  }, [isIncrementDisabled, max, value, onChange]);

  const baseButtonSx = useMemo(
    () => ({
      borderRadius: 2,
      width: config.buttonSize,
      height: config.buttonSize,
      "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.1)",
      },
      "&:disabled": {
        borderColor: "divider",
      },
    }),
    [color, config.buttonSize]
  );

  const baseQuantitySx = useMemo(
    () => ({
      minWidth: config.buttonSize,
      textAlign: "center" as const,
      fontSize: config.fontSize,
      fontWeight: 700,
    }),
    [color, config.fontSize, config.buttonSize]
  );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: config.gap,
        userSelect: "none",
        backgroundColor: "rgba(0, 0, 0, 0.01)",
        backdropFilter: "blur(14px)",
        height: config.height,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
        px: 0.5,
        ...containerSx,
      }}
      className={className}
      {...props}
    >
      <IconButton
        onClick={handleDecrement}
        disabled={isDecrementDisabled}
        sx={{
          ...baseButtonSx,
          ...buttonSx,
        }}
        aria-label="Decrease quantity"
      >
        <RemoveIcon />
      </IconButton>

      <Typography
        sx={{
          ...baseQuantitySx,
          ...quantitySx,
        }}
      >
        {value}
      </Typography>

      <IconButton
        onClick={handleIncrement}
        disabled={isIncrementDisabled}
        sx={{
          ...baseButtonSx,
          ...buttonSx,
        }}
        aria-label="Increase quantity"
      >
        <AddIcon />
      </IconButton>
    </Box>
  );
};

export const QuantityControl = QuantityControlComponent;

QuantityControl.displayName = "QuantityControl";
